import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Sprout, Gift, Check, Loader2, Copy, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { publicApi } from "@/lib/api";
import { formatIDR } from "@/lib/format";

export default function QROptin() {
  const { campaignId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", whatsapp_number: "", email: "", order_code: "", consent_status: true });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    publicApi.getCampaign(campaignId)
      .then(setData)
      .catch(() => toast.error("Campaign tidak ditemukan"))
      .finally(() => setLoading(false));
  }, [campaignId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.whatsapp_number) {
      toast.error("Nama dan WhatsApp wajib diisi");
      return;
    }
    setSubmitting(true);
    try {
      const res = await publicApi.optin(campaignId, {
        ...form,
        source_platform: data.campaign.source_platform,
        last_product_bought: data.campaign.product_name,
        campaign_id: campaignId,
      });
      setSuccess(res);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-emerald-600" size={32} /></div>;

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center">
          <div className="text-4xl mb-3">😔</div>
          <h1 className="font-heading text-2xl font-bold text-gray-900">Campaign tidak ditemukan</h1>
          <p className="mt-2 text-gray-600">Link QR mungkin sudah tidak aktif atau kadaluarsa.</p>
        </div>
      </div>
    );
  }

  const { campaign, merchant } = data;

  if (success) {
    const rewardText = campaign.reward_type === "cashback"
      ? `Cashback ${formatIDR(campaign.reward_value)}`
      : campaign.reward_type === "points"
      ? `${campaign.reward_value} Poin`
      : campaign.reward_type === "voucher"
      ? `Voucher ${formatIDR(campaign.reward_value)}`
      : "Free Gift";

    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-white flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 sm:p-8 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
              <Check className="text-emerald-600" size={32} strokeWidth={2.5} />
            </div>
            <h1 className="mt-5 font-heading text-2xl font-bold text-gray-900">Selamat, {form.name.split(" ")[0]}!</h1>
            <p className="mt-2 text-gray-600 text-sm">Anda resmi jadi member <span className="font-semibold text-gray-900">{merchant.business_name}</span></p>

            <div className="mt-6 bg-gradient-to-br from-amber-50 to-emerald-50 border border-amber-200 rounded-xl p-5">
              <div className="text-xs font-bold tracking-widest uppercase text-amber-700">Reward Anda</div>
              <div className="mt-2 font-heading text-2xl font-bold text-gray-900">{rewardText}</div>
              <div className="mt-3 bg-white border border-dashed border-gray-300 rounded-lg p-3">
                <div className="text-xs text-gray-500 mb-1">Kode reward</div>
                <div className="flex items-center justify-center gap-2">
                  <code className="font-mono text-lg font-bold text-emerald-700" data-testid="reward-code">{success.reward.reward_code}</code>
                  <button
                    onClick={() => { navigator.clipboard.writeText(success.reward.reward_code); toast.success("Kode disalin"); }}
                    className="text-gray-400 hover:text-gray-700"
                    data-testid="copy-reward-code"
                  >
                    <Copy size={16} />
                  </button>
                </div>
              </div>
              <p className="mt-3 text-xs text-gray-600">Simpan kode ini untuk digunakan di pembelian berikutnya.</p>
            </div>

            <div className="mt-6 space-y-2 text-left">
              <div className="flex items-start gap-2 text-sm text-gray-700">
                <Check className="text-emerald-600 mt-0.5 flex-shrink-0" size={16} strokeWidth={2.5} />
                <span>Anda akan menerima info promo & reward via WhatsApp</span>
              </div>
              <div className="flex items-start gap-2 text-sm text-gray-700">
                <Check className="text-emerald-600 mt-0.5 flex-shrink-0" size={16} strokeWidth={2.5} />
                <span>Reminder repeat order otomatis sesuai siklus produk</span>
              </div>
              <div className="flex items-start gap-2 text-sm text-gray-700">
                <Check className="text-emerald-600 mt-0.5 flex-shrink-0" size={16} strokeWidth={2.5} />
                <span>Akses ke campaign eksklusif member</span>
              </div>
            </div>

            <Button className="mt-6 w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold" asChild data-testid="chat-owner-btn">
              <a href={`https://wa.me/${(merchant.phone || "").replace(/\D/g, "")}?text=Halo,%20saya%20${form.name}%20baru%20jadi%20member.%20Kode%20reward:%20${success.reward.reward_code}`} target="_blank" rel="noreferrer">
                <MessageCircle className="mr-2 h-4 w-4" /> Chat Owner via WhatsApp
              </a>
            </Button>
          </div>
          <div className="text-center mt-4 text-xs text-gray-400">Powered by <span className="font-semibold text-emerald-600">Custora</span></div>
        </div>
      </div>
    );
  }

  const rewardBadge = campaign.reward_type === "cashback"
    ? `Cashback ${formatIDR(campaign.reward_value)}`
    : campaign.reward_type === "voucher"
    ? `Voucher ${formatIDR(campaign.reward_value)}`
    : campaign.reward_type === "points"
    ? `${campaign.reward_value} Poin`
    : "Free Gift";

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-white">
      <div className="max-w-md mx-auto p-4 pt-6">
        <div className="flex items-center gap-2 justify-center">
          <div className="h-8 w-8 rounded-lg gradient-emerald flex items-center justify-center">
            <Sprout className="text-white" strokeWidth={2.5} size={18} />
          </div>
          <span className="font-heading font-bold text-gray-900">{merchant.business_name}</span>
        </div>

        <div className="mt-6 bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-br from-emerald-500 to-emerald-700 text-white p-6 relative overflow-hidden">
            <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)", backgroundSize: "16px 16px" }} />
            <div className="relative">
              <div className="inline-flex items-center gap-1.5 bg-white/20 backdrop-blur px-2.5 py-1 rounded-full text-xs font-semibold">
                <Gift size={12} /> Reward untuk Anda
              </div>
              <h1 className="mt-3 font-heading text-2xl font-bold leading-tight">{rewardBadge}</h1>
              <p className="mt-1 text-emerald-50 text-sm">Terima kasih sudah belanja! Scan berhasil, isi form untuk klaim reward Anda.</p>
            </div>
          </div>

          <form onSubmit={submit} className="p-6 space-y-4">
            <div>
              <Label htmlFor="name">Nama Lengkap*</Label>
              <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Contoh: Sarah Wijaya" className="mt-1.5 h-11" data-testid="optin-name-input" />
            </div>
            <div>
              <Label htmlFor="wa">Nomor WhatsApp*</Label>
              <Input id="wa" required value={form.whatsapp_number} onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })}
                placeholder="081234567890" className="mt-1.5 h-11" data-testid="optin-whatsapp-input" />
              <p className="mt-1 text-xs text-gray-500">Untuk kirim kode reward & info repeat order</p>
            </div>
            <div>
              <Label htmlFor="email">Email (opsional)</Label>
              <Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="email@contoh.com" className="mt-1.5 h-11" data-testid="optin-email-input" />
            </div>
            <div>
              <Label htmlFor="order">Kode Order (opsional)</Label>
              <Input id="order" value={form.order_code} onChange={(e) => setForm({ ...form, order_code: e.target.value })}
                placeholder="Nomor pesanan marketplace" className="mt-1.5 h-11" data-testid="optin-order-input" />
            </div>

            {campaign.product_name && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm">
                <span className="text-gray-500">Produk:</span> <span className="font-semibold text-gray-900">{campaign.product_name}</span>
              </div>
            )}

            <div className="flex items-start gap-2 pt-2">
              <Checkbox id="consent" checked={form.consent_status} onCheckedChange={(v) => setForm({ ...form, consent_status: !!v })} data-testid="optin-consent" />
              <label htmlFor="consent" className="text-xs text-gray-600 leading-relaxed cursor-pointer">
                Saya setuju menerima promo, reward, dan informasi produk dari {merchant.business_name} melalui WhatsApp.
              </label>
            </div>

            <Button type="submit" disabled={submitting || !form.consent_status}
              className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-base"
              data-testid="optin-submit-button">
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Klaim Reward Sekarang
            </Button>
          </form>
        </div>

        <div className="text-center mt-6 text-xs text-gray-400">
          Powered by <span className="font-semibold text-emerald-600">Custora</span> — Customer Ownership Platform
        </div>
      </div>
    </div>
  );
}
